import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";

import { combineClassNames, isNullOrUndefined } from "../../utils/appUtils";

import WPDropdown from "../WPDropdown";
import WPInputHelperText from "../WPInputHelperText";
import WPInputLabel from "../WPInputLabel";

import CloseCircleGreenIcon from "../../common/icons/CloseCircleGreenIcon";
import CloseCircleRedIcon from "../../common/icons/CloseCircleRedIcon";
import CircularLoader from "../../common/styles/components/circularLoader";
import TooltipRenderer from "../../components/TooltipRenderer";

import "./WPInput.scss";

const WPInput = (
  {
    id,
    name,
    type = "text",
    label,
    value: initialValue,
    disabled,
    onChange,
    onKeyDown,
    onBlur,
    onFocus,
    placeholder,
    className,
    prefix,
    suffix,
    required,
    requiredMsg,
    isLoading,
    tabIndex,
    allowDecimals = true,
    allowNegative,
    roundOff = 2,
    validate,
    helperText,
    maxLength,
    minLength,
    validateOnMount = true,
    externalError, // use this incase you want a custom UX validation for the input intstead of the default one
    externalWarning,
    clearIcon,
    onValidationError,
    customValidation,
    dropdown,
    showRangeAlways,
    hideRangeAlways,
    isCalcEngine,
    wrapperClassName,
    dataTestId,
    maxDecimals,
  },
  ref,
) => {
  const [value, setValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const [validationVal, setValidationVal] = useState({
    error: null,
    warning: null,
    default: null,
  });

  const { error, warning, default: defaultHelper } = validationVal || {};

  const isError = externalError || !isNullOrUndefined(error);

  const isWarning = externalWarning || warning;
  const isRequired = Boolean(required || requiredMsg);

  const getHelperTextValue = useMemo(() => {
    if ((disabled && !showRangeAlways) || hideRangeAlways) return null;
    return (
      error ||
      externalError ||
      externalWarning ||
      warning ||
      defaultHelper ||
      helperText ||
      ""
    );
  }, [
    disabled,
    error,
    warning,
    externalError,
    externalWarning,
    defaultHelper,
    helperText,
  ]);

  const displayValue = useMemo(() => {
    if (type === "number") {
      return isFocused
        ? value
        : !isNullOrUndefined(roundOff)
          ? parseFloat(value).toFixed(roundOff)
          : value;
    }
    return value;
  }, [value, type, isFocused, roundOff]);

  const handleChange = (event) => {
    const { value } = event.target;
    if (allowDecimals && value.includes(".") && maxDecimals) {
      const decimalPart = value.split(".")[1];
      if (decimalPart && decimalPart.length > maxDecimals) {
        return;
      }
    }
    if (customValidation && !customValidation(value)) {
      return;
    }
    setValue(value);
    onChange?.(event);
  };

  const handleFocus = (event) => {
    setIsFocused(true);
    onFocus?.(event);
  };

  /**
   * Calls the `onValidationError` callback with the helper text value if there is an error.
   * If no error exists, it passes `null` to indicate no validation error.
   */
  useEffect(() => {
    if (onValidationError) {
      const errMsg = isError ? getHelperTextValue : null;
      onValidationError(errMsg, id, dataTestId);
    }
  }, [isError]);

  const handleBlur = (event) => {
    setIsFocused(false);
    if (isRequired && value?.trim?.() === "") {
      setValidationVal({ error: requiredMsg });
    } else if (validate && !externalError) {
      setValidationVal(validate(value));
      onValidationError?.(isError ? getHelperTextValue : null, id, dataTestId);
    }
    onBlur?.(event);
  };

  //By default Browser allows 'e' input key as its a math exponent , arrow keys are added to prevent increment and decrement of value
  const numberInvalidKeys = useMemo(() => {
    const keys = ["e", "E", "+", "ArrowUp", "ArrowDown"];
    if (!allowDecimals) {
      keys.push(".");
    }
    if (!allowNegative) {
      keys.push("-");
    }
    return keys;
  }, [allowDecimals, allowNegative]);

  const handleKeyDown = (event) => {
    const { key } = event;
    if (type === "number" && numberInvalidKeys.includes(key)) {
      event.preventDefault();
    }
    if (key === "Enter") {
      event.target.blur();
      //handleBlur(event); // It was triggering handle Blur twice
    }
    onKeyDown?.(event);
  };

  const handleClear = () => {
    handleChange({ target: { value: "" } });
  };

  const checkValidation = useCallback(() => {
    if (showRangeAlways && validate) {
      setValidationVal(validate(value));
    } else if (validateOnMount && isRequired && value?.trim?.() === "") {
      setValidationVal({ error: requiredMsg });
    } else if (validateOnMount && validate && !externalError) {
      setValidationVal(validate(value));
    } else {
      setValidationVal({ error: null, warning: null, default: null });
    }
  }, [
    showRangeAlways,
    validate,
    value,
    validateOnMount,
    isRequired,
    requiredMsg,
    externalError,
  ]);

  useEffect(() => {
    const validationHandler = setTimeout(() => {
      checkValidation();
    }, 150);

    return () => clearTimeout(validationHandler);
  }, [checkValidation]);

  useEffect(() => {
    setValue(initialValue ?? "");
  }, [initialValue]);

  const inputElement = (
    <input
      id={id}
      ref={ref}
      type={type}
      name={name ?? id}
      tabIndex={tabIndex}
      maxLength={maxLength}
      minLength={minLength}
      required={isRequired}
      placeholder={placeholder}
      value={displayValue ?? ""}
      disabled={disabled}
      onKeyDown={handleKeyDown}
      onWheel={(event) => event.target.blur()}
      data-is-error={Boolean(isError)}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onChange={handleChange}
      className={combineClassNames(
        "wp-input fs-14",
        className,
        isLoading && "isLoading",
      )}
      data-test-id={dataTestId}
    />
  );

  return (
    <div className="wp-input-container">
      {label && (
        <WPInputLabel
          required={required}
          label={label}
          disabled={disabled}
          id={id}
        />
      )}
      <div
        className={combineClassNames(
          "wp-input-wrapper",
          isWarning && !isError && "warning",
          isError && !disabled && "error",
          disabled && "disabled",
          dropdown && "has-dropdown",
          isCalcEngine && "calc-engine",
          wrapperClassName,
        )}
      >
        {prefix && !isLoading && !dropdown && (
          <span
            className={combineClassNames(
              "wp-input-prefix fs-14",
              disabled ? "disabled" : "",
            )}
          >
            {prefix}
          </span>
        )}
        <TooltipRenderer title={displayValue}>{inputElement}</TooltipRenderer>
        {isLoading && <CircularLoader className="wp-input-icon" size="20px" />}
        {clearIcon && !isLoading && !disabled && (
          <div className="wp-input-icon clear-icon" onClick={handleClear}>
            {isError ? <CloseCircleRedIcon /> : <CloseCircleGreenIcon />}
          </div>
        )}
        {suffix && !isLoading && !dropdown && (
          <span
            className={combineClassNames(
              "wp-input-suffix fs-14",
              disabled ? "disabled" : "",
            )}
          >
            {suffix}
          </span>
        )}

        {dropdown && (
          <div className="wp-input-dropdown">
            <WPDropdown
              {...dropdown}
              disabled={disabled || dropdown.disabled}
              tabIndex={tabIndex}
            />
          </div>
        )}
      </div>

      {getHelperTextValue && (
        <WPInputHelperText
          id={id}
          isError={isError}
          isWarning={isWarning}
          text={getHelperTextValue}
        />
      )}
    </div>
  );
};

const ForwardedWPInput = forwardRef(WPInput);

ForwardedWPInput.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  type: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  handleBlur: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  suffix: PropTypes.string,
  required: PropTypes.bool,
  isLoading: PropTypes.bool,
  tabIndex: PropTypes.number,
  allowDecimals: PropTypes.bool,
  roundOff: PropTypes.number,
  validate: PropTypes.func,
  helperText: PropTypes.string,
  maxLength: PropTypes.number,
  minLength: PropTypes.number,
  validateOnMount: PropTypes.bool,
  isError: PropTypes.bool,
  clearIcon: PropTypes.bool,
  wrapperClassName: PropTypes.string,
};

export default ForwardedWPInput;
